import { css } from '@emotion/react';
import { FC, useState, useRef, useEffect } from 'react';
import { Box, Flex, Text, Tabs, TabList, Tab, TabPanels, TabPanel, Select } from '@chakra-ui/react';
import { isNil } from 'lodash';
import { FinancialStatement, Stats } from '../../../apis/backend';
import { calculateChangeColor, colors, fontStyles, spacing } from '../../../styles';
import formatDisplay, { FormatterStyle } from '../../../utils/formatDisplay';

interface Props {
  data: FinancialStatement[] | undefined;
  stats: Stats | undefined;
  periodType: 'quarterly' | 'annual' | 'ttm';
  onPeriodChange: (type: 'quarterly' | 'annual' | 'ttm') => void;
  currency?: string;
}

type StatementRow = {
  label: string;
  key: keyof FinancialStatement | 'computed' | 'ttm_only';
  isComputed?: boolean;
  computeFn?: (statement: FinancialStatement) => number | null;
  ttmValue?: (stats: Stats) => number | null;
  isMargin?: boolean;
  isGrowth?: boolean;
  indent?: boolean;
  isSectionHeader?: boolean;
  isSubtotal?: boolean;
  isFinalTotal?: boolean;
  ttmOnly?: boolean; // Only show in TTM column
  isPointInTime?: boolean; // For balance sheet items that shouldn't be summed for TTM
};

type MetricRow = {
  label: string;
  value: (stats: Stats) => number | null;
  format: FormatterStyle;
  isGrowth?: boolean;
  isSectionHeader?: boolean;
};

// Helper to sum last 4 quarters for TTM
const sumTTM = (statements: FinancialStatement[], key: keyof FinancialStatement): number | null => {
  // TTM requires exactly 4 quarters of data
  if (statements.length < 4) return null;
  const values = statements.slice(0, 4).map((s) => s[key] as number | null);
  if (values.every((v) => v === null)) return null;
  return values.reduce((sum, v) => (sum ?? 0) + (v ?? 0), 0);
};

const INCOME_ROWS: StatementRow[] = [
  { label: 'Total Revenue', key: 'totalRevenue' },
  {
    label: 'Revenue Growth',
    key: 'ttm_only',
    ttmOnly: true,
    isGrowth: true,
    indent: true,
    ttmValue: (s) => s.revenueGrowth ?? s.threeYearRevenueGrowth,
  },
  { label: 'Cost of Revenue', key: 'costOfRevenue' },
  { label: 'Gross Profit', key: 'grossProfit', isSubtotal: true },
  {
    label: 'Gross Margin',
    key: 'computed',
    isComputed: true,
    isMargin: true,
    indent: true,
    computeFn: (s) =>
      s.grossProfit !== null && s.totalRevenue !== null && s.totalRevenue !== 0 ? s.grossProfit / s.totalRevenue : null,
    ttmValue: (s) => s.grossMargin,
  },
  { label: 'Operating Expenses', key: 'operatingExpenses' },
  { label: 'R&D', key: 'researchAndDevelopment', indent: true },
  {
    label: 'R&D % of Revenue',
    key: 'ttm_only',
    ttmOnly: true,
    isMargin: true,
    indent: true,
    ttmValue: (s) => s.rdMargin1y,
  },
  { label: 'SG&A', key: 'sellingGeneralAndAdministrative', indent: true },
  {
    label: 'Marketing % of Revenue',
    key: 'ttm_only',
    ttmOnly: true,
    isMargin: true,
    indent: true,
    ttmValue: (s) => s.salesMargin1y,
  },
  { label: 'Operating Income', key: 'operatingIncome', isSubtotal: true },
  {
    label: 'Operating Margin',
    key: 'computed',
    isComputed: true,
    isMargin: true,
    indent: true,
    computeFn: (s) =>
      s.operatingIncome !== null && s.totalRevenue !== null && s.totalRevenue !== 0
        ? s.operatingIncome / s.totalRevenue
        : null,
  },
  { label: 'EBITDA', key: 'ebitda' },
  { label: 'Net Income', key: 'netIncome', isFinalTotal: true },
  {
    label: 'Net Margin',
    key: 'computed',
    isComputed: true,
    isMargin: true,
    indent: true,
    computeFn: (s) =>
      s.netIncome !== null && s.totalRevenue !== null && s.totalRevenue !== 0 ? s.netIncome / s.totalRevenue : null,
    ttmValue: (s) => s.netMargin,
  },
  {
    label: 'Earnings Growth',
    key: 'ttm_only',
    ttmOnly: true,
    isGrowth: true,
    indent: true,
    ttmValue: (s) => s.earningsGrowth ?? s.threeYearEarningsGrowth,
  },
];

const BALANCE_ROWS: StatementRow[] = [
  { label: 'ASSETS', key: 'computed', isComputed: true, computeFn: () => null, isSectionHeader: true },
  { label: 'Cash', key: 'cash', indent: true, isPointInTime: true },
  { label: 'Cash & Short-term Inv.', key: 'cashAndShortTermInvestments', indent: true, isPointInTime: true },
  { label: 'Net Receivables', key: 'netReceivables', indent: true, isPointInTime: true },
  { label: 'Inventory', key: 'inventory', indent: true, isPointInTime: true },
  { label: 'Total Current Assets', key: 'totalCurrentAssets', isSubtotal: true, isPointInTime: true },
  { label: 'PP&E', key: 'propertyPlantEquipment', indent: true, isPointInTime: true },
  { label: 'Goodwill', key: 'goodwill', indent: true, isPointInTime: true },
  { label: 'Total Assets', key: 'totalAssets', isSubtotal: true, isPointInTime: true },
  { label: 'LIABILITIES', key: 'computed', isComputed: true, computeFn: () => null, isSectionHeader: true },
  { label: 'Accounts Payable', key: 'accountsPayable', indent: true, isPointInTime: true },
  { label: 'Deferred Revenue', key: 'deferredRevenue', indent: true, isPointInTime: true },
  { label: 'Short-term Debt', key: 'shortTermDebt', indent: true, isPointInTime: true },
  { label: 'Total Current Liabilities', key: 'totalCurrentLiabilities', isSubtotal: true, isPointInTime: true },
  { label: 'Long-term Debt', key: 'longTermDebt', indent: true, isPointInTime: true },
  { label: 'Total Liabilities', key: 'totalLiabilities', isSubtotal: true, isPointInTime: true },
  { label: 'EQUITY', key: 'computed', isComputed: true, computeFn: () => null, isSectionHeader: true },
  { label: 'Common Stock', key: 'commonStock', indent: true, isPointInTime: true },
  { label: 'Retained Earnings', key: 'retainedEarnings', indent: true, isPointInTime: true },
  { label: 'Total Equity', key: 'totalShareholderEquity', isFinalTotal: true, isPointInTime: true },
];

const BALANCE_METRICS: MetricRow[] = [
  { label: 'FINANCIAL HEALTH', value: () => null, format: FormatterStyle.moneyBig, isSectionHeader: true },
  { label: 'Total Capital', value: (s) => s.totalCapital, format: FormatterStyle.moneyBig },
  { label: 'Cash / Debt Ratio', value: (s) => s.cashToDebt, format: FormatterStyle.number },
  { label: 'Equity / Debt Ratio', value: (s) => s.equityToDebt, format: FormatterStyle.number },
  { label: 'EFFICIENCY', value: () => null, format: FormatterStyle.moneyBig, isSectionHeader: true },
  { label: 'Return on Equity', value: (s) => s.returnOnEquity, format: FormatterStyle.percent },
  { label: 'Return on Assets', value: (s) => s.returnOnAssets, format: FormatterStyle.percent },
  { label: 'Return on Capital', value: (s) => s.returnOnCapital, format: FormatterStyle.percent },
];

const CASHFLOW_ROWS: StatementRow[] = [
  { label: 'OPERATING', key: 'computed', isComputed: true, computeFn: () => null, isSectionHeader: true },
  { label: 'Depreciation & Amort.', key: 'depreciationAndAmortization', indent: true },
  { label: 'Operating Cash Flow', key: 'operatingCashflow', isSubtotal: true },
  {
    label: 'Op. Cashflow Margin',
    key: 'ttm_only',
    ttmOnly: true,
    isMargin: true,
    indent: true,
    ttmValue: (s) => s.operatingCashflowMargin1y,
  },
  { label: 'INVESTING', key: 'computed', isComputed: true, computeFn: () => null, isSectionHeader: true },
  { label: 'Capital Expenditures', key: 'capitalExpenditures', indent: true },
  {
    label: 'Capex to Revenue',
    key: 'ttm_only',
    ttmOnly: true,
    isMargin: true,
    indent: true,
    ttmValue: (s) => s.capexToRevenue,
  },
  { label: 'Investing Cash Flow', key: 'cashflowFromInvestment', isSubtotal: true },
  { label: 'FINANCING', key: 'computed', isComputed: true, computeFn: () => null, isSectionHeader: true },
  { label: 'Financing Cash Flow', key: 'cashflowFromFinancing', isSubtotal: true },
  { label: 'Net Change in Cash', key: 'changeInCash', isSubtotal: true },
  {
    label: 'Free Cash Flow',
    key: 'computed',
    isComputed: true,
    isFinalTotal: true,
    computeFn: (s) => {
      if (s.operatingCashflow === null || s.capitalExpenditures === null) return null;
      return s.operatingCashflow - Math.abs(s.capitalExpenditures);
    },
    ttmValue: (s) => s.freeCashFlow,
  },
  {
    label: 'FCF Margin',
    key: 'ttm_only',
    ttmOnly: true,
    isMargin: true,
    indent: true,
    ttmValue: (s) => s.freeCashflowMargin,
  },
  {
    label: 'CF Return on Capital',
    key: 'ttm_only',
    ttmOnly: true,
    isMargin: true,
    indent: true,
    ttmValue: (s) => s.cashflowReturnOnCapital,
  },
  {
    label: 'Cashflow Yield',
    key: 'ttm_only',
    ttmOnly: true,
    isMargin: true,
    indent: true,
    ttmValue: (s) => s.cashflowYield,
  },
];

const VALUATION_METRICS: MetricRow[] = [
  { label: 'PRICE RATIOS', value: () => null, format: FormatterStyle.number, isSectionHeader: true },
  { label: 'Price / Earnings', value: (s) => s.priceToEarnings, format: FormatterStyle.number },
  { label: 'Price / Book', value: (s) => s.priceToBook, format: FormatterStyle.number },
  { label: 'Price / Sales', value: (s) => s.priceToSales, format: FormatterStyle.number },
  { label: 'P/S Growth', value: (s) => s.priceToSalesGrowth, format: FormatterStyle.number },
  { label: 'PEG', value: (s) => s.priceToEarningsGrowth, format: FormatterStyle.number },
  { label: 'EV / EBITDA', value: (s) => s.evToEbitda, format: FormatterStyle.number },
  { label: 'EV / Free Cash Flow', value: (s) => s.evToCashflow, format: FormatterStyle.number },
  { label: 'EV / Gross Profit', value: (s) => s.evToGrossProfit, format: FormatterStyle.number },
  { label: 'EV / Sales', value: (s) => s.evToSales, format: FormatterStyle.number },
  { label: 'YIELDS', value: () => null, format: FormatterStyle.number, isSectionHeader: true },
  { label: 'Earnings Yield', value: (s) => s.earningsYield, format: FormatterStyle.percentDetailed },
  { label: 'Dividend Yield', value: (s) => s.dividendYield, format: FormatterStyle.percentDetailed },
  { label: 'SHARE INFO', value: () => null, format: FormatterStyle.number, isSectionHeader: true },
  { label: 'Enterprise Value', value: (s) => s.enterpriseValue, format: FormatterStyle.moneyBig },
  { label: 'Market Cap', value: (s) => s.marketCap, format: FormatterStyle.moneyBig },
  { label: 'Share Growth (1Y)', value: (s) => s.shareGrowth1q, format: FormatterStyle.percentChange, isGrowth: true },
  { label: 'Share Growth Q/Q', value: (s) => s.shareGrowthQq, format: FormatterStyle.percentChange, isGrowth: true },
  { label: 'Beta', value: (s) => s.beta, format: FormatterStyle.number },
  { label: 'Short Percent', value: (s) => s.shortPercent, format: FormatterStyle.percent },
];

const INSIDER_METRICS: MetricRow[] = [
  { label: 'INSIDER ACTIVITY (3mo)', value: () => null, format: FormatterStyle.moneyBig, isSectionHeader: true },
  { label: 'Insider Buys', value: (s) => s.dollarsBought, format: FormatterStyle.moneyBig },
  { label: 'Insider Sells', value: (s) => s.dollarsSold, format: FormatterStyle.moneyBig },
  { label: 'Insider Exercises', value: (s) => s.dollarsExercised, format: FormatterStyle.moneyBig },
  { label: 'Insider Grants', value: (s) => s.dollarsGranted, format: FormatterStyle.moneyBig },
  { label: 'Other', value: (s) => s.dollarsOther, format: FormatterStyle.moneyBig },
  { label: 'Total Bought or Sold', value: (s) => s.netDollarsTraded, format: FormatterStyle.moneyBig },
  { label: '% of Market Cap Traded', value: (s) => s.netPercentTraded, format: FormatterStyle.percentDetailed },
];

const formatQuarterLabel = (dateStr: string, isAnnual: boolean): string => {
  const date = new Date(dateStr);
  const year = date.getFullYear().toString().slice(-2);
  if (isAnnual) {
    return `FY'${year}`;
  }
  const month = date.getMonth();
  const quarter = Math.floor(month / 3) + 1;
  return `Q${quarter}'${year}`;
};

const getValue = (statement: FinancialStatement, row: StatementRow): number | null => {
  if (row.isComputed && row.computeFn) {
    return row.computeFn(statement);
  }
  if (row.ttmOnly) {
    return null;
  }
  return statement[row.key as keyof FinancialStatement] as number | null;
};

const getTTMValue = (row: StatementRow, statements: FinancialStatement[], stats: Stats | undefined): number | null => {
  // For TTM-only rows, use stats value
  if (row.ttmOnly && row.ttmValue && stats) {
    return row.ttmValue(stats);
  }
  // For rows with explicit TTM value from stats, use that
  if (row.ttmValue && stats) {
    return row.ttmValue(stats);
  }
  // For point-in-time values (balance sheet items), use most recent quarter instead of summing
  if (row.isPointInTime && row.key !== 'computed' && row.key !== 'ttm_only' && statements.length > 0) {
    return statements[0][row.key as keyof FinancialStatement] as number | null;
  }
  // For computed rows, compute from TTM data
  if (row.isComputed && row.computeFn) {
    // Create a synthetic TTM statement by summing quarters
    const ttmStatement: Partial<FinancialStatement> = {};
    const keys: (keyof FinancialStatement)[] = [
      'totalRevenue',
      'costOfRevenue',
      'grossProfit',
      'operatingExpenses',
      'operatingIncome',
      'netIncome',
      'ebitda',
      'operatingCashflow',
      'capitalExpenditures',
    ];
    keys.forEach((key) => {
      (ttmStatement as any)[key] = sumTTM(statements, key);
    });
    return row.computeFn(ttmStatement as FinancialStatement);
  }
  // For regular rows, sum the TTM
  if (row.key !== 'computed' && row.key !== 'ttm_only') {
    return sumTTM(statements, row.key as keyof FinancialStatement);
  }
  return null;
};

const formatValue = (value: number | null, isMargin: boolean, isGrowth?: boolean, currency?: string): string => {
  if (value === null || isNil(value)) return '—';
  if (isMargin || isGrowth) {
    return formatDisplay(FormatterStyle.percent, value) || '—';
  }
  return formatDisplay(FormatterStyle.moneyCompact, value, currency) || '—';
};

// Check if a row has any displayable data
const rowHasData = (
  row: StatementRow,
  periods: FinancialStatement[],
  stats: Stats | undefined,
  showTTM: boolean,
): boolean => {
  // Section headers are always shown
  if (row.isSectionHeader) return true;

  // TTM-only rows only show if we have TTM column and TTM value
  if (row.ttmOnly) {
    if (!showTTM) return false;
    const ttmVal = row.ttmValue && stats ? row.ttmValue(stats) : null;
    return ttmVal !== null;
  }

  // Check if any period has a value
  const hasPeriodsData = periods.some((p) => {
    const val = getValue(p, row);
    return val !== null;
  });

  if (hasPeriodsData) return true;

  // Check TTM value if applicable
  if (showTTM) {
    const ttmVal = getTTMValue(row, periods, stats);
    if (ttmVal !== null) return true;
  }

  return false;
};

const StatementTable: FC<{
  rows: StatementRow[];
  statements: FinancialStatement[];
  stats: Stats | undefined;
  isAnnual: boolean;
  periodType: 'quarterly' | 'annual' | 'ttm';
  onPeriodChange: (type: 'quarterly' | 'annual' | 'ttm') => void;
  currency?: string;
}> = ({ rows, statements, stats, isAnnual, periodType, onPeriodChange, currency }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isTTMMode = periodType === 'ttm';

  // Take up to 4 most recent periods (oldest to newest order for display)
  const periods = statements.slice(0, 4).reverse();
  // Show TTM column for quarterly view or TTM mode
  const showTTM = !isAnnual || isTTMMode;
  // In TTM mode, don't show individual period columns
  const showPeriods = !isTTMMode;

  // Filter rows to only show those with data
  const visibleRows = rows.filter((row) => rowHasData(row, periods, stats, showTTM));

  // Scroll to the right on mount to show most recent data
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [periods.length]);

  if (periods.length === 0) {
    return (
      <Text css={fontStyles.cat} color={colors.tungsten}>
        No data available
      </Text>
    );
  }

  return (
    <Box css={styles.tableContainer} ref={scrollRef}>
      <table css={styles.table}>
        <thead>
          <tr>
            <th css={[styles.headerCell, styles.stickyLabelCell, styles.periodSelectorCell]}>
              <Select
                size="xs"
                value={periodType}
                onChange={(e) => onPeriodChange(e.target.value as 'quarterly' | 'annual' | 'ttm')}
                css={styles.periodSelect}
                w="auto"
                borderColor={colors.cloud}
                _dark={{ borderColor: colors.tungsten }}
              >
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
                <option value="ttm">TTM</option>
              </Select>
            </th>
            {showPeriods &&
              periods.map((p) => (
                <th key={p.fiscalDate} css={styles.headerCell}>
                  {formatQuarterLabel(p.fiscalDate, p.isAnnual)}
                </th>
              ))}
            {showTTM && <th css={[styles.headerCell, isTTMMode ? undefined : styles.ttmHeaderCell]}>TTM</th>}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row, idx) => {
            const isSectionHeader = row.isSectionHeader === true;

            return (
              <tr key={idx} css={[isSectionHeader && styles.sectionRow, row.isMargin && styles.marginRow]}>
                <td
                  css={[
                    styles.labelCell,
                    styles.stickyLabelCell,
                    row.indent && styles.indentedCell,
                    isSectionHeader && styles.sectionLabel,
                    (row.isSubtotal || row.isFinalTotal) && styles.subtotalLabel,
                  ]}
                >
                  {row.label}
                </td>
                {showPeriods &&
                  periods.map((p) => {
                    if (isSectionHeader) {
                      return <td key={p.fiscalDate} css={styles.valueCell}></td>;
                    }
                    if (row.ttmOnly) {
                      return <td key={p.fiscalDate} css={styles.valueCell}></td>;
                    }
                    const value = getValue(p, row);
                    const marginColor = row.isMargin && value !== null ? calculateChangeColor(value) : undefined;
                    return (
                      <td
                        key={p.fiscalDate}
                        css={[
                          styles.valueCell,
                          row.isSubtotal && styles.subtotalValue,
                          row.isFinalTotal && styles.finalTotalValue,
                        ]}
                        style={marginColor ? { color: marginColor } : undefined}
                      >
                        {formatValue(value, !!row.isMargin, row.isGrowth, currency)}
                      </td>
                    );
                  })}
                {showTTM && (
                  <td
                    css={[
                      styles.valueCell,
                      !isTTMMode && styles.ttmCell,
                      row.isSubtotal && styles.subtotalValue,
                      row.isFinalTotal && styles.finalTotalValue,
                    ]}
                    style={
                      (row.isMargin || row.isGrowth) && getTTMValue(row, statements, stats) !== null
                        ? { color: calculateChangeColor(getTTMValue(row, statements, stats)!) }
                        : undefined
                    }
                  >
                    {isSectionHeader
                      ? ''
                      : formatValue(getTTMValue(row, statements, stats), !!row.isMargin, row.isGrowth, currency)}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

const MetricsTable: FC<{ metrics: MetricRow[]; stats: Stats | undefined; currency?: string }> = ({
  metrics,
  stats,
  currency,
}) => {
  if (!stats) {
    return (
      <Text css={fontStyles.cat} color={colors.tungsten}>
        No data available
      </Text>
    );
  }

  return (
    <Box css={styles.metricsContainer}>
      {metrics.map((metric, idx) => {
        if (metric.isSectionHeader) {
          return (
            <Box key={idx} css={styles.metricSectionHeader}>
              {metric.label}
            </Box>
          );
        }
        const value = metric.value(stats);
        const displayValue = isNil(value) ? '—' : formatDisplay(metric.format, value, currency) || '—';
        const valueColor = metric.isGrowth && value !== null ? calculateChangeColor(value) : undefined;

        return (
          <Flex key={idx} css={styles.metricRow} justify="space-between">
            <Text css={styles.metricLabel}>{metric.label}</Text>
            <Text css={styles.metricValue} style={valueColor ? { color: valueColor } : undefined}>
              {displayValue}
            </Text>
          </Flex>
        );
      })}
    </Box>
  );
};

const FinancialStatements: FC<Props> = ({ data, stats, periodType, onPeriodChange, currency }) => {
  const [tabIndex, setTabIndex] = useState(0);

  if (!data || data.length === 0) {
    return (
      <Box css={styles.container}>
        <Text css={fontStyles.cat} color={colors.tungsten}>
          No financial statement data available
        </Text>
      </Box>
    );
  }

  const isAnnual = periodType === 'annual';

  return (
    <Box css={styles.container}>
      <Tabs index={tabIndex} onChange={setTabIndex} variant="unstyled" css={styles.tabs}>
        <TabList css={styles.tabListInner}>
          <Tab css={[styles.tab, tabIndex === 0 && styles.tabSelected]}>
            <span css={styles.tabLabelFull}>Income</span>
            <span css={styles.tabLabelShort}>Income</span>
          </Tab>
          <Tab css={[styles.tab, tabIndex === 1 && styles.tabSelected]}>
            <span css={styles.tabLabelFull}>Balance</span>
            <span css={styles.tabLabelShort}>Bal.</span>
          </Tab>
          <Tab css={[styles.tab, tabIndex === 2 && styles.tabSelected]}>
            <span css={styles.tabLabelFull}>Cash Flow</span>
            <span css={styles.tabLabelShort}>Cash</span>
          </Tab>
          <Tab css={[styles.tab, tabIndex === 3 && styles.tabSelected]}>
            <span css={styles.tabLabelFull}>Valuation</span>
            <span css={styles.tabLabelShort}>Value</span>
          </Tab>
          <Tab css={[styles.tab, tabIndex === 4 && styles.tabSelected]}>
            <span css={styles.tabLabelFull}>Insiders</span>
            <span css={styles.tabLabelShort}>Insider</span>
          </Tab>
        </TabList>

        <TabPanels>
          <TabPanel p={0} pt={2}>
            <StatementTable
              rows={INCOME_ROWS}
              statements={data}
              stats={stats}
              isAnnual={isAnnual}
              periodType={periodType}
              onPeriodChange={onPeriodChange}
              currency={currency}
            />
          </TabPanel>
          <TabPanel p={0} pt={2}>
            <StatementTable
              rows={BALANCE_ROWS}
              statements={data}
              stats={stats}
              isAnnual={isAnnual}
              periodType={periodType}
              onPeriodChange={onPeriodChange}
              currency={currency}
            />
            {stats && <MetricsTable metrics={BALANCE_METRICS} stats={stats} currency={currency} />}
          </TabPanel>
          <TabPanel p={0} pt={2}>
            <StatementTable
              rows={CASHFLOW_ROWS}
              statements={data}
              stats={stats}
              isAnnual={isAnnual}
              periodType={periodType}
              onPeriodChange={onPeriodChange}
              currency={currency}
            />
          </TabPanel>
          <TabPanel p={0} pt={2}>
            <MetricsTable metrics={VALUATION_METRICS} stats={stats} currency={currency} />
          </TabPanel>
          <TabPanel p={0} pt={2}>
            <MetricsTable metrics={INSIDER_METRICS} stats={stats} currency={currency} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

const styles = {
  container: css`
    margin-bottom: ${spacing.deci};
  `,
  tabs: css``,
  tabListInner: css`
    display: flex;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    border-bottom: 1px solid ${colors.cloud};
    html[data-theme='dark'] & {
      border-color: ${colors.tungsten};
    }
    &::-webkit-scrollbar {
      display: none;
    }
  `,
  tab: css`
    ${fontStyles.mouse};
    color: ${colors.tungsten};
    padding: ${spacing.centi} ${spacing.deci};
    border-bottom: 2px solid transparent;
    margin-bottom: -1px;
    white-space: nowrap;
    flex-shrink: 0;
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
  tabLabelFull: css`
    display: none;
    @media (min-width: 400px) {
      display: inline;
    }
  `,
  tabLabelShort: css`
    display: inline;
    @media (min-width: 400px) {
      display: none;
    }
  `,
  tabSelected: css`
    color: ${colors.petrolBlue};
    border-bottom-color: ${colors.petrolBlue};
    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
      border-bottom-color: ${colors.skyBlue};
    }
  `,
  tableContainer: css`
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    position: relative;
  `,
  table: css`
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    ${fontStyles.cat};
  `,
  headerCell: css`
    text-align: right;
    padding: ${spacing.milli} ${spacing.centi};
    ${fontStyles.flea};
    color: ${colors.tungsten};
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
  stickyLabelCell: css`
    position: sticky;
    left: 0;
    background-color: ${colors.sugar};
    z-index: 1;
    text-align: left;
    html[data-theme='dark'] & {
      background-color: ${colors.liquorice};
    }
  `,
  periodSelectorCell: css`
    padding: ${spacing.milli} ${spacing.centi};
  `,
  periodSelect: css`
    ${fontStyles.flea};
    height: 24px;
    min-height: 24px;
    border-radius: 4px;
  `,
  ttmHeaderCell: css`
    background-color: ${colors.fog};
    html[data-theme='dark'] & {
      background-color: rgba(255, 255, 255, 0.05);
    }
  `,
  labelCell: css`
    text-align: left;
    padding: ${spacing.milli} ${spacing.centi};
    white-space: nowrap;
  `,
  indentedCell: css`
    padding-left: ${spacing.deci};
    color: ${colors.tungsten};
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
  valueCell: css`
    text-align: right;
    padding: ${spacing.milli} ${spacing.centi};
    white-space: nowrap;
  `,
  ttmCell: css`
    background-color: ${colors.fog};
    html[data-theme='dark'] & {
      background-color: rgba(255, 255, 255, 0.05);
    }
  `,
  marginRow: css``,
  sectionRow: css`
    border-top: 1px solid ${colors.cloud};
    html[data-theme='dark'] & {
      border-color: ${colors.tungsten};
    }
  `,
  sectionLabel: css`
    ${fontStyles.flea};
    color: ${colors.tungsten};
    padding-top: ${spacing.centi};
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
  subtotalLabel: css``,
  subtotalValue: css`
    border-top: 1px solid ${colors.tungsten};
    html[data-theme='dark'] & {
      border-color: ${colors.cloud};
    }
  `,
  finalTotalValue: css`
    border-top: 3px double ${colors.tungsten};
    html[data-theme='dark'] & {
      border-color: ${colors.cloud};
    }
  `,
  metricsContainer: css`
    margin-top: ${spacing.deci};
    min-height: 200px;
  `,
  metricSectionHeader: css`
    ${fontStyles.flea};
    color: ${colors.tungsten};
    padding: ${spacing.centi} 0 ${spacing.milli} 0;
    border-top: 1px solid ${colors.cloud};
    margin-top: ${spacing.centi};
    html[data-theme='dark'] & {
      color: ${colors.cloud};
      border-color: ${colors.tungsten};
    }
    &:first-of-type {
      border-top: none;
      margin-top: 0;
    }
  `,
  metricRow: css`
    padding: ${spacing.milli} 0;
  `,
  metricLabel: css`
    ${fontStyles.cat};
    min-width: 150px;
  `,
  metricValue: css`
    ${fontStyles.cat};
  `,
};

export default FinancialStatements;
